// src/hooks/init/useRetailerInitialization.js
import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import * as storage from '../../browserStorage';
import { storageItemName } from '../../config';
import { getExistingCheckout, consoleDebug } from '../../util';

export const useRetailerInitialization = ({
    retailers,
    nonHeadlessRetailers,
    shortcodeData,
    isSingleRetailer,
    singleRetailerId,
    getRetailerBySlug,
}) => {
    const params = useParams();
    const [isInitialized, setIsInitialized] = useState(false);
    const [retailerId, setRetailerId] = useState(null);
    const [locationSelected, setLocationSelected] = useState(false);
    const [nonHeadlessRetailer, setNonHeadlessRetailer] = useState(
        storage.getItem('NON_HEADLESS_RETAILER')
    );
    const [nonHeadlessRetailerData, setNonHeadlessRetailerData] = useState(null);

    // Initialize retailer on mount
    useEffect(() => {
        // Handle case where retailers object is completely missing or null
        if (!retailers) {
            console.error('❌ CRITICAL: Retailers data is null or undefined. This indicates a backend configuration issue.');
            setIsInitialized(true); // Mark as initialized to prevent infinite loop
            return;
        }

        // Handle case where retailers is empty (multi-key not configured properly)
        if (Object.keys(retailers).length === 0) {
            console.error('❌ CRITICAL: No retailers found. If multi-key is enabled, ensure "Clear API Cache" has been run.');
            setIsInitialized(true); // Mark as initialized so LocationGate can show
            setLocationSelected(false); // Ensure LocationGate will display
            return;
        }

        if (isInitialized) return;

        consoleDebug('🔍 Initializing retailer selection', {
            hasRetailers: !!retailers,
            retailerCount: Object.keys(retailers).length,
            isSingleRetailer,
            params,
            shortcodeData,
            locationSlug: params?.locationSlug,
            categoryType: params?.categoryType,
            categoryName: params?.categoryName,
            fullPath: window.location.pathname
        });

        let newRetailerId = null;
        let foundRetailer = false;

        // Priority 1: From shortcode
        const retailerShortcode = Object.values(shortcodeData).find(
            data => data.shortcode === 'headlessproducts' && data?.retailerShortcode?.providedId
        )?.retailerShortcode?.providedId;

        if (retailerShortcode) {
            newRetailerId = retailerShortcode;
            foundRetailer = true;
            consoleDebug('✅ Found retailer from shortcode:', retailerShortcode);
        }
        // Priority 2: From URL - check this BEFORE existing checkout
        else if (params?.locationSlug) {
            consoleDebug('🔍 Checking URL params for retailer:', {
                locationSlug: params.locationSlug,
                categoryType: params.categoryType,
                categoryName: params.categoryName,
                allRetailerSlugs: Object.values(retailers).map(r => r.menuSlug)
            });

            // Check if locationSlug is actually a retailer slug
            const retailerMatch = getRetailerBySlug(params.locationSlug);
            if (retailerMatch) {
                newRetailerId = retailerMatch.id;
                foundRetailer = true;
                consoleDebug('✅ Found retailer from URL slug:', params.locationSlug, 'ID:', retailerMatch.id);

                // Check if we have an existing checkout for a DIFFERENT retailer
                const checkout = getExistingCheckout();
                if (checkout?.retailerId && checkout.retailerId !== retailerMatch.id) {
                    consoleDebug('🔄 URL retailer differs from cart retailer, clearing cart:', {
                        urlRetailer: retailerMatch.id,
                        urlRetailerSlug: params.locationSlug,
                        cartRetailer: checkout.retailerId,
                        checkoutId: checkout.checkoutId
                    });
                    // Clear the old cart - it will be recreated in CartContext
                    storage.removeItem(storageItemName);
                }
            } else {
                consoleDebug('⚠️ No retailer match found for slug:', params.locationSlug);

                // If not a retailer slug, check if it's a filter URL
                const categoryTypes = ['brand', 'category', 'tags', 'effect', 'specials', 'subcategory'];
                if (categoryTypes.includes(params.locationSlug)) {
                    // Check existing checkout for filter URLs
                    const filterCheckout = getExistingCheckout();
                    if (filterCheckout?.retailerId && retailers[filterCheckout.retailerId]) {
                        newRetailerId = filterCheckout.retailerId;
                        foundRetailer = true;
                        consoleDebug('✅ Found retailer from checkout for filter URL:', filterCheckout.retailerId);
                    }
                    // If still no retailer and it's a single retailer site, use the single retailer
                    else if (isSingleRetailer && singleRetailerId) {
                        newRetailerId = singleRetailerId;
                        foundRetailer = true;
                        consoleDebug('✅ Using single retailer for filter URL:', singleRetailerId);
                    }
                }
            }
        }
        // Priority 3: Check existing checkout (only if no URL retailer found)
        else {
            const checkout = getExistingCheckout();
            if (checkout?.retailerId && retailers[checkout.retailerId]) {
                newRetailerId = checkout.retailerId;
                foundRetailer = true;
                consoleDebug('✅ Found retailer from existing checkout:', checkout.retailerId);
            }
        }

        // Priority 4: Single retailer
        if (!newRetailerId && isSingleRetailer) {
            newRetailerId = singleRetailerId;
            foundRetailer = true;
            consoleDebug('✅ Using single retailer:', singleRetailerId);
        }

        // If we found a retailer, set it
        if (newRetailerId) {
            setRetailerId(newRetailerId);
            setLocationSelected(true);
            consoleDebug('✅ Retailer set:', newRetailerId);
        } else {
            // Before showing location gate, double-check localStorage one more time
            const finalCheckout = getExistingCheckout();
            if (finalCheckout?.retailerId && retailers[finalCheckout.retailerId]) {
                setRetailerId(finalCheckout.retailerId);
                setLocationSelected(true);
                consoleDebug('✅ Found retailer from final checkout check:', finalCheckout.retailerId);
            } else {
                // No retailer found - this is important for multi-retailer sites
                consoleDebug('⚠️ No retailer found - will show location gate');
                setLocationSelected(false);
            }
        }

        // Check for non-headless retailer
        const currentPath = window.location.pathname;
        if (/^\/location\//.test(currentPath) && !retailerShortcode) {
            const matchingRetailer = Object.values(nonHeadlessRetailers).find(retailer =>
                window.location.href.startsWith(retailer.non_headless_location_homepage)
            );

            if (matchingRetailer) {
                setNonHeadlessRetailer(matchingRetailer.location_name);
                setNonHeadlessRetailerData(matchingRetailer);
                consoleDebug('✅ Found non-headless retailer:', matchingRetailer.location_name);
            }
        }

        setIsInitialized(true);
        consoleDebug('✅ Retailer initialization complete', {
            retailerId: newRetailerId,
            locationSelected: foundRetailer,
            nonHeadlessRetailer: !!nonHeadlessRetailer
        });
    }, [retailers, params?.locationSlug, params?.categoryType, params?.categoryName, shortcodeData, isSingleRetailer, singleRetailerId, nonHeadlessRetailers, getRetailerBySlug, isInitialized]);

    // Handle URL-based retailer changes after initialization
    useEffect(() => {
        if (!isInitialized || !retailers || !params?.locationSlug) return;

        consoleDebug('🔍 Checking for retailer change from URL:', {
            currentRetailerId: retailerId,
            urlSlug: params.locationSlug,
            categoryType: params.categoryType
        });

        // Check if the URL contains a retailer slug
        const urlRetailerMatch = getRetailerBySlug(params.locationSlug);
        if (urlRetailerMatch && urlRetailerMatch.id !== retailerId) {
            consoleDebug('🔄 Detected retailer change from URL navigation:', {
                oldRetailerId: retailerId,
                newRetailerId: urlRetailerMatch.id,
                newRetailerSlug: params.locationSlug
            });

            // Clear existing checkout if it's for a different retailer
            const checkout = getExistingCheckout();
            if (checkout?.retailerId && checkout.retailerId !== urlRetailerMatch.id) {
                consoleDebug('🗑️ Clearing cart for retailer change');
                storage.removeItem(storageItemName);
            }

            // Update retailer
            setRetailerId(urlRetailerMatch.id);
            setLocationSelected(true);
        }
    }, [params?.locationSlug, params?.categoryType, retailerId, isInitialized, retailers, getRetailerBySlug]);

    return {
        isInitialized,
        retailerId,
        setRetailerId,
        locationSelected,
        setLocationSelected,
        nonHeadlessRetailer,
        setNonHeadlessRetailer,
        nonHeadlessRetailerData,
        setNonHeadlessRetailerData,
    };
};
